import React, {
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from 'react'

import _throttle from 'lodash.throttle'
import { useWebRendererContext } from './WebRendererContext'

interface WebRendererCameraContext {
  zoom: number
  setZoom: (zoom: number) => void
  setZoomToFit: () => void
}

export const canvasCameraPositionContext = React.createContext<WebRendererCameraContext>(
  {} as WebRendererCameraContext
)

type WebRendererCameraContextProviderProps = {
  children: React.ReactNode
}

/**
 * Context provider to handle the camera zoom position changes
 * and expose the zoom value and some functions to set the zoom level.
 * We are using a separate context than the WebRendererContext for performance
 * reasons (We are expecting the states in this context to update very often
 * when the camera moves).
 */
export function WebRendererCameraContextProvider({
  children,
}: WebRendererCameraContextProviderProps) {
  const { renderer } = useWebRendererContext()

  // Initial value set in the state doesn't matted because
  // it will be overwritten when setting the renderer
  const [zoom, updateZoomState] = useState<number>(0)
  useSubscribeToCameraChanges(updateZoomState)

  const setZoom = useCallback(
    (zoomFactor?: number) => {
      if (zoomFactor && renderer) {
        renderer.gestureManager.setZoom(zoomFactor)
      }
    },
    [renderer]
  )

  const setZoomToFit = useCallback(() => {
    if (renderer) {
      renderer.zoomToFit()
    }
  }, [renderer])

  const context: WebRendererCameraContext = {
    zoom,
    setZoom,
    setZoomToFit,
  }

  return (
    <canvasCameraPositionContext.Provider value={context}>
      {children}
    </canvasCameraPositionContext.Provider>
  )
}

/**
 * Get or set the web canvas renderer zoom level from the context.
 */
export function usePageCanvasZoom() {
  return useContext(canvasCameraPositionContext)
}

/**
 * Listen for "zoom" and "pan" events coming from the web-renderer
 * and save the value changes to the URL and to the local react state (zoom only).
 * Note: we are debouncing the write to the URL and throttling the write
 * to the react local state to avoid performance issues.
 */
export function useSubscribeToCameraChanges(
  updateLocalZoomState: (zoom: number) => void
) {
  const { renderer } = useWebRendererContext()

  // Throttle to avoid spamming the UI with react state changes
  // that could hurt performance. 100ms was picked arbitrarily to
  // batch zoom changes event while maintaining a smooth update
  // of value of the zoom control component in the header.
  const updateLocalZoomStateThrottled = useMemo(
    () => _throttle(updateLocalZoomState, 100),
    [updateLocalZoomState]
  )

  // Listen for zoom changes coming from the Web Renderer to propagate them
  // to the UI.
  useEffect(() => {
    if (!renderer) {
      return
    }

    function onZoomChanged(zoom: number) {
      updateLocalZoomStateThrottled(zoom)
    }

    renderer.gestureManager.on('zoom', onZoomChanged)
    return () => {
      renderer?.gestureManager.off('zoom', onZoomChanged)
    }
  }, [renderer, updateLocalZoomState, updateLocalZoomStateThrottled])
}
